/**
 * Keşif (web crawler) — TypeScript mirror of the backend wire contract.
 *
 * Source of truth: `services/macdm-orchestrator/src/macdm/crawler/models.py`
 * and `crawler/events.py`. The wire is camelCase on purpose so no mapping
 * layer exists here — a mapping layer is where field renames go to hide.
 *
 * These are TYPES ONLY. Runtime validation lives in
 * `transport/crawlEventCodec.ts`; the two are kept in step by
 * `scripts/verify_crawl_codec.ts` plus the backend parity test
 * `tests/test_crawl_events_codec_parity.py`. Both exist because a type and a
 * validator are two sources of truth and `tsc` cannot compare them.
 */

/* ---------- atoms ---------- */

export type CrawlScopeMode =
  | "exact-host"
  | "subdomains"
  | "registrable-domain"
  | "allowlist"
  | "unrestricted";

export type CrawlRenderMode = "auto" | "http-only" | "browser-always";

export type CrawlFetchTier = "cache" | "http" | "browser" | "screenshot";

export type CrawlAssetKind =
  | "image"
  | "pdf"
  | "document"
  | "video"
  | "audio"
  | "archive"
  | "other";

export type CrawlJobStatus =
  | "queued"
  | "running"
  | "paused"
  | "completed"
  | "cancelled"
  | "failed";

export type CrawlJobPhase =
  | "bootstrap"
  | "planning"
  | "crawling"
  | "saturating"
  | "indexing"
  | "finalizing"
  | "done";

export type CrawlPageType =
  | "home"
  | "article"
  | "listing"
  | "product"
  | "docs"
  | "contact"
  | "legal"
  | "media"
  | "search"
  | "other";

export type CrawlFrontierStatus = "pending" | "leased" | "done" | "failed" | "skipped";

export type CorpusStalenessClass =
  | "news"
  | "social"
  | "market"
  | "reference"
  | "academic"
  | "regulation";

/** A status no longer advances on its own; polling and streams can stop. */
export const TERMINAL_STATUSES: readonly CrawlJobStatus[] = [
  "completed",
  "cancelled",
  "failed",
];

export function isTerminalStatus(s: CrawlJobStatus): boolean {
  return TERMINAL_STATUSES.includes(s);
}

/* ---------- policy ---------- */

/**
 * Everything the wizard configures. Mirrors `CrawlPolicy` field-for-field,
 * including the two authorisation gates the backend enforces in a
 * `model_validator`: `respectRobots=false` and `requestsPerSecond > 2`
 * both require `ownerDeclared=true` and are rejected with 422 otherwise.
 */
export interface CrawlPolicy {
  // scope
  scopeMode: CrawlScopeMode;
  allowedDomains: string[];
  blockedDomains: string[];
  includePatterns: string[];
  excludePatterns: string[];
  maxDepth: number;
  maxPages: number;
  maxPagesPerHost: number;
  maxExternalHops: number;
  followSitemaps: boolean;

  // politeness
  requestsPerSecond: number;
  respectRobots: boolean;
  ownerDeclared: boolean;
  maxDurationMinutes: number;

  // rendering
  renderMode: CrawlRenderMode;
  captureScreenshots: boolean;
  /** `null` inherits the server default; `0` forces a real re-fetch. */
  recrawlTtlHours: number | null;

  // extraction
  extractAssets: boolean;
  assetKinds: CrawlAssetKind[];
  extractTables: boolean;
  extractStructuredData: boolean;
  minTextChars: number;

  // LLM
  intent: string;
  llmPlan: boolean;
  llmPrioritise: boolean;
  llmExtract: boolean;
  extractSchema: Record<string, unknown> | null;

  // output
  pushToCorpus: boolean;
  corpusStalenessClass: CorpusStalenessClass;

  // stop conditions
  adaptiveStop: boolean;
}

/**
 * Defaults duplicated from `CrawlPolicy`'s Pydantic field defaults so the
 * wizard can render a complete form without a round-trip. The backend
 * re-validates every field regardless, so a drift here is a UI inconvenience
 * and never a security or budget bypass.
 */
export const CRAWL_POLICY_DEFAULTS: CrawlPolicy = {
  scopeMode: "subdomains",
  allowedDomains: [],
  blockedDomains: [],
  includePatterns: [],
  excludePatterns: [],
  maxDepth: 3,
  maxPages: 250,
  maxPagesPerHost: 2000,
  maxExternalHops: 0,
  followSitemaps: true,

  requestsPerSecond: 1.0,
  respectRobots: true,
  ownerDeclared: false,
  maxDurationMinutes: 60,

  renderMode: "auto",
  captureScreenshots: false,
  recrawlTtlHours: null,

  extractAssets: true,
  assetKinds: ["image", "pdf", "document"],
  extractTables: true,
  extractStructuredData: true,
  minTextChars: 180,

  intent: "",
  llmPlan: true,
  llmPrioritise: true,
  llmExtract: false,
  extractSchema: null,

  pushToCorpus: true,
  corpusStalenessClass: "reference",

  adaptiveStop: true,
};

/** Bounds mirrored from the Pydantic `Field(ge=…, le=…)` constraints. */
export const CRAWL_LIMITS = {
  maxDepth: { min: 0, max: 32 },
  maxPages: { min: 1, max: 50_000 },
  maxPagesPerHost: { min: 1, max: 50_000 },
  maxExternalHops: { min: 0, max: 3 },
  requestsPerSecond: { min: 0.1, max: 20 },
  maxDurationMinutes: { min: 1, max: 1440 },
  minTextChars: { min: 0, max: 100_000 },
  recrawlTtlHours: { min: 0, max: 8760 },
  seeds: { max: 25 },
  patterns: { max: 40, length: 200 },
  /** Above this RPS the backend demands `ownerDeclared`. */
  rpsWithoutOwnership: 2.0,
} as const;

/* ---------- wire shapes ---------- */

export interface CrawlJobCounters {
  discovered: number;
  fetched: number;
  failed: number;
  skipped: number;
  duplicate: number;
  pending: number;
  assetsSaved: number;
  assetsSkipped: number;
  bytesFetched: number;
  assetBytes: number;
  llmCalls: number;
  corpusRows: number;
  hostsSeen: number;
}

export const EMPTY_COUNTERS: CrawlJobCounters = {
  discovered: 0,
  fetched: 0,
  failed: 0,
  skipped: 0,
  duplicate: 0,
  pending: 0,
  assetsSaved: 0,
  assetsSkipped: 0,
  bytesFetched: 0,
  assetBytes: 0,
  llmCalls: 0,
  corpusRows: 0,
  hostsSeen: 0,
};

export interface CrawlHost {
  host: string;
  allowed: boolean;
  disallowReason: string;
  robotsStatus: number | null;
  robotsBypass: boolean;
  crawlDelayS: number | null;
  effectiveDelayS: number;
  sitemapCount: number;
  pagesFetched: number;
  bytesFetched: number;
  consecutiveErrors: number;
  circuitOpen: boolean;
}

export interface CrawlJob {
  id: string;
  name: string;
  seedUrls: string[];
  policy: CrawlPolicy;
  status: CrawlJobStatus;
  phase: CrawlJobPhase;
  counters: CrawlJobCounters;
  plan: Record<string, unknown>;
  error: string;
  degradedReason: string;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  updatedAt: string | null;
  /** Present only in list responses: an engine is running in this process. */
  live?: boolean;
}

export interface CrawlPage {
  id: string;
  url: string;
  finalUrl: string;
  host: string;
  depth: number;
  httpStatus: number | null;
  contentType: string;
  title: string;
  description: string;
  lang: string;
  author: string;
  publishedAt: string | null;
  charCount: number;
  wordCount: number;
  pageType: CrawlPageType;
  fetchTier: CrawlFetchTier;
  fetchMs: number;
  qualityScore: number;
  outlinkCount: number;
  assetCount: number;
  duplicateOf: string | null;
  llmSummary: string;
  llmTopics: string[];
  structured: Record<string, unknown>;
  error: string;
  fetchedAt: string;
  /** Detail endpoint only — omitted from list payloads. */
  extractedText?: string | null;
  markdown?: string | null;
}

export interface CrawlAsset {
  id: string;
  pageId: string | null;
  url: string;
  kind: CrawlAssetKind;
  role: string;
  contentType: string;
  bytes: number;
  width: number | null;
  height: number | null;
  altText: string;
  caption: string;
  visionSummary: string;
  status: string;
  skipReason: string;
  /** ACL-checked backend route. Crawled bytes are never served from webroot. */
  downloadUrl: string;
  createdAt: string;
}

/**
 * LLM planning output. An editable SUGGESTION — the backend never
 * auto-applies it, so the wizard must render it as an accept/reject diff.
 */
export interface CrawlPlanSuggestion {
  summary: string;
  suggestedMaxDepth: number | null;
  suggestedMaxPages: number | null;
  includePatterns: string[];
  excludePatterns: string[];
  priorityKeywords: string[];
  sitemapUrls: string[];
  detectedSiteType: string;
  warnings: string[];
}

export interface CrawlFrontierStats {
  pending: number;
  leased: number;
  done: number;
  failed: number;
  skipped: number;
}

/* ---------- SSE events ---------- */

export type CrawlLogLevel = "info" | "warn" | "error";

export interface CrawlJobEvent {
  type: "crawl.job";
  jobId: string;
  status: CrawlJobStatus;
  phase: CrawlJobPhase;
  counters: CrawlJobCounters;
  name: string;
  error: string;
  degradedReason: string;
}

export interface CrawlProgressEvent {
  type: "crawl.progress";
  jobId: string;
  counters: CrawlJobCounters;
  phase: CrawlJobPhase;
  /**
   * Best-effort 0..1 estimate. Honest about being an estimate: the
   * denominator is `fetched + pending`, which GROWS as links are discovered,
   * so it can move backwards early in a crawl. Render it as a bar, never as
   * an ETA the user is invited to trust.
   */
  progress: number;
  activeHosts: number;
  inFlight: number;
  pagesPerMinute: number;
  etaSeconds: number | null;
}

export interface CrawlPageEvent {
  type: "crawl.page";
  jobId: string;
  page: CrawlPage;
}

export interface CrawlAssetEvent {
  type: "crawl.asset";
  jobId: string;
  pageId: string | null;
  assets: CrawlAsset[];
}

export interface CrawlHostEvent {
  type: "crawl.host";
  jobId: string;
  host: CrawlHost;
}

export interface CrawlPlanEvent {
  type: "crawl.plan";
  jobId: string;
  plan: CrawlPlanSuggestion;
}

export interface CrawlLogEvent {
  type: "crawl.log";
  jobId: string;
  level: CrawlLogLevel;
  message: string;
  url: string;
  /** Unix-ms. */
  at: number;
}

export interface CrawlSaturatedEvent {
  type: "crawl.saturated";
  jobId: string;
  novelRatio: number;
  windowSize: number;
  reason: string;
}

export interface CrawlDoneEvent {
  type: "crawl.done";
  jobId: string;
  status: CrawlJobStatus;
  counters: CrawlJobCounters;
  stopReason: string;
  elapsedMs: number;
  summary: Record<string, unknown>;
}

export interface CrawlErrorEvent {
  type: "crawl.error";
  jobId: string;
  code: string;
  message: string;
}

export type CrawlEvent =
  | CrawlJobEvent
  | CrawlProgressEvent
  | CrawlPageEvent
  | CrawlAssetEvent
  | CrawlHostEvent
  | CrawlPlanEvent
  | CrawlLogEvent
  | CrawlSaturatedEvent
  | CrawlDoneEvent
  | CrawlErrorEvent;

/* ---------- display helpers ---------- */

export const SCOPE_LABELS: Record<CrawlScopeMode, string> = {
  "exact-host": "Yalnızca bu host",
  subdomains: "Alt alan adları dahil",
  "registrable-domain": "Tüm alan adı",
  allowlist: "Yalnızca izin listesi",
  unrestricted: "Sınırsız",
};

export const STATUS_LABELS: Record<CrawlJobStatus, string> = {
  queued: "Sırada",
  running: "Çalışıyor",
  paused: "Duraklatıldı",
  completed: "Tamamlandı",
  cancelled: "İptal edildi",
  failed: "Başarısız",
};

/** Chip tone per status. Kept beside the labels so the two never drift. */
export const STATUS_TONES: Record<
  CrawlJobStatus,
  "neutral" | "violet" | "green" | "amber" | "rose" | "cyan"
> = {
  queued: "violet",
  running: "green",
  paused: "amber",
  completed: "cyan",
  cancelled: "neutral",
  failed: "rose",
};

export const PHASE_LABELS: Record<CrawlJobPhase, string> = {
  bootstrap: "Hazırlık",
  planning: "Planlama",
  crawling: "Tarama",
  saturating: "Doygunluk",
  indexing: "Dizinleme",
  finalizing: "Sonlandırma",
  done: "Bitti",
};

export const TIER_LABELS: Record<CrawlFetchTier, string> = {
  cache: "Önbellek",
  http: "HTTP",
  browser: "Tarayıcı",
  screenshot: "Ekran görüntüsü",
};

export const PAGE_TYPE_LABELS: Record<CrawlPageType, string> = {
  home: "Anasayfa",
  article: "Makale",
  listing: "Liste",
  product: "Ürün",
  docs: "Doküman",
  contact: "İletişim",
  legal: "Hukuki",
  media: "Medya",
  search: "Arama",
  other: "Diğer",
};

export const ASSET_KIND_LABELS: Record<CrawlAssetKind, string> = {
  image: "Görsel",
  pdf: "PDF",
  document: "Belge",
  video: "Video",
  audio: "Ses",
  archive: "Arşiv",
  other: "Diğer",
};

/** Binary units, because that is what a disk quota is measured in. */
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const exp = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  const value = n / Math.pow(1024, exp);
  return `${value.toFixed(exp === 0 ? 0 : value < 10 ? 1 : 0)} ${units[exp]}`;
}

export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  const s = Math.round(seconds);
  if (s < 60) return `${s} sn`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} dk ${s % 60} sn`;
  const h = Math.floor(m / 60);
  return `${h} sa ${m % 60} dk`;
}
